/**
 * Order dashboard and card payment dialog.
 *
 * Use HTTPS for `environment.api_url` in non-local deployments.
 *
 * SECURITY: Prefer strong response headers on routes that handle payment data
 * (e.g. Content-Security-Policy, Referrer-Policy: no-referrer,
 * Cache-Control: no-store) — typically configured on the API or reverse proxy.
 *
 * TODO(PCI): Move card capture to CyberSource Microform v2 (or similar) so the
 * PAN and CVV never pass through this application or custom backend code.
 */

import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { pairwise, startWith } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DataService } from '../services/data.service';
import { JdeOrder } from './model/jde-order.model';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import {
  AutoCompleteModule,
  type AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { PaymentData } from './model/payment-data.model';
import { PaymentResponse } from './model/payment-response.model';
import {
  amountMaxValidator,
  cvvLengthValidator,
  luhnValidator,
  notExpiredValidator,
  stateForCountryValidator,
  zipForCountryValidator,
} from './payment-form.validators';
import {
  CardBrand,
  detectCardFromPanInput,
  stripCardNumberSpaces,
} from './card-utils';
import {
  CA_PROVINCES,
  type StateProvinceOption,
  US_STATES,
} from './state-province-options';
import { COUNTRIES, type CountryOption } from './country-options';
import { CacheService } from '../services/cache.service';
import { NotificationMessage } from '../services/model/notification-message.model';
import { DialogType } from '../services/model/dialog-type.enum';
import { CdiPaymentStatus } from './model/cdi-payment-status.enum';

/** Allowed characters for the URL segment after /dashboard/ (alphanumeric, underscore, hyphen). */
const SECTION_PATTERN = /^[a-zA-Z0-9_-]+$/;
const LOOKUP_MAX_LEN = 50;

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    AutoCompleteModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly _data = inject(DataService);

  public showInvoiceSection: boolean = false;
  public showMakePaymentButton: boolean = false;
  public lookupId = '';
  public order?: JdeOrder;
  public displayPaymentDialog = false;
  public displayPaymentConfirmationDialog = false;
  public submitting = false;
  public detectedBrand: CardBrand = 'unknown';

  public filteredStates: StateProvinceOption[] = [];
  public filteredCountries: CountryOption[] = [];

  public readonly monthOptions = Array.from({ length: 12 }, (_, i) => ({
    label: String(i + 1).padStart(2, '0'),
    value: i + 1,
  }));

  /** Includes prior year so expired combinations remain selectable for correction flows. */
  public readonly yearOptions = (() => {
    const y = new Date().getFullYear();
    const start = y - 1;
    return Array.from({ length: 22 }, (_, i) => ({
      label: String(start + i),
      value: start + i,
    }));
  })();

  public readonly paymentForm = this.fb.group(
    {
      firstName: ['', [Validators.required, Validators.maxLength(60)]],
      lastName: ['', [Validators.required, Validators.maxLength(60)]],
      streetAddress1: ['', [Validators.required, Validators.maxLength(60)]],
      streetAddress2: ['', [Validators.maxLength(60)]],
      city: ['', [Validators.required, Validators.maxLength(60)]],
      state: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          stateForCountryValidator,
        ],
      ],
      zipCode: ['', [Validators.required, zipForCountryValidator]],
      country: ['US', [Validators.required, Validators.maxLength(2)]],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(120)],
      ],
      cardNumber: ['', [Validators.required, luhnValidator]],
      cardType: this.fb.control({ value: '001', disabled: true }),
      expirationMonth: [null as number | null, Validators.required],
      expirationYear: [null as number | null, Validators.required],
      cvv: ['', [Validators.required, cvvLengthValidator(3)]],
      amount: this.fb.control(
        { value: null as number | null, disabled: true },
        [Validators.required, Validators.min(0.01)],
      ),
      currency: this.fb.control(
        { value: 'USD', disabled: true },
        [Validators.required, Validators.pattern(/^[A-Za-z]{3}$/)],
      ),
    },
    { validators: [notExpiredValidator] },
  );

  public paymentResponse?: PaymentResponse;

  constructor(
    private readonly _cache: CacheService,
    private readonly _router: Router,
  ) {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const raw = params.get('lookupId');
      this.lookupId = this.sanitizeSection(raw);
      if (
        this.lookupId.toUpperCase() !== 'NO_VALUE_PROVIDED' &&
        this.lookupId.toUpperCase() !== 'INVALID_VALUE'
      ) {
        // Forward the HMAC signature query params from the emailed link so the API can validate them.
        const exp = this.route.snapshot.queryParamMap.get('exp') ?? '';
        const sig = this.route.snapshot.queryParamMap.get('sig') ?? '';
        this._data
          .get<JdeOrder>(
            `api/dashboard/lookup/${this.lookupId}?exp=${encodeURIComponent(exp)}&sig=${encodeURIComponent(sig)}`,
            'Retrieving data ...',
            false,
          )
          .subscribe({
            next: (data) => {
              this.order = data;
              switch (this.order.cdiPaymentStatus) {
                case CdiPaymentStatus.PaymentSuccessful:
                case CdiPaymentStatus.PaymentComplete:
                  this.showMakePaymentButton = false;
                  console.log('switch', this.order.cdiPaymentStatus);
                  break;
                default:
                  this.showMakePaymentButton = true;
                  break;
              }

              this.showInvoiceSection = true;
              this.refreshAmountValidators();
            },
            error: (err) => {
              throw err;
            },
          });
      }
    });

    this.paymentForm
      .get('country')
      ?.valueChanges.pipe(
        startWith(this.paymentForm.get('country')?.value),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([prev, next]) => {
        const a = (prev ?? '').toString().toUpperCase();
        const b = (next ?? '').toString().toUpperCase();
        if (a !== b) {
          this.paymentForm.get('state')?.setValue('');
        }
        this.applyAddressValidatorsForCountry();
      });
  }

  filterStates(event: { query: string }): void {
    const q = (event.query ?? '').trim().toLowerCase();
    const list = this.allStatesForCountry();
    if (!q) {
      this.filteredStates = [...list];
      return;
    }
    this.filteredStates = list.filter(
      (o) =>
        o.code.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
    );
  }

  onStateSelect(event: AutoCompleteSelectEvent): void {
    const opt = event.value as StateProvinceOption;
    if (opt?.code) {
      this.paymentForm.get('state')?.setValue(opt.code);
    }
  }

  filterCountries(event: { query: string }): void {
    const q = (event.query ?? '').trim().toLowerCase();
    if (!q) {
      this.filteredCountries = [...COUNTRIES];
      return;
    }
    this.filteredCountries = COUNTRIES.filter(
      (o) =>
        o.code.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
    );
  }

  onCountrySelect(event: AutoCompleteSelectEvent): void {
    const opt = event.value as CountryOption;
    if (opt?.code) {
      this.paymentForm.get('country')?.setValue(opt.code);
    }
  }

  private allStatesForCountry(): StateProvinceOption[] {
    const c = (this.paymentForm.get('country')?.value ?? '')
      .toString()
      .toUpperCase();
    if (c === 'US') {
      return US_STATES;
    }
    if (c === 'CA') {
      return CA_PROVINCES;
    }
    return [];
  }

  /** US and CA use a state/province dropdown; other countries use optional free text. */
  public usesSubdivisionDropdown(): boolean {
    const c = (this.paymentForm.get('country')?.value ?? '')
      .toString()
      .toUpperCase();
    return c === 'US' || c === 'CA';
  }

  /** Display value for the payment dialog; matches the amount submitted from the form. */
  public get amountValue(): number | null {
    return this.paymentForm.getRawValue().amount;
  }

  public brandLabel(): string {
    switch (this.detectedBrand) {
      case 'visa':
        return 'Visa';
      case 'mastercard':
        return 'Mastercard';
      case 'amex':
        return 'American Express';
      case 'discover':
        return 'Discover';
      default:
        return '—';
    }
  }

  public brandIconClass(): string {
    switch (this.detectedBrand) {
      case 'visa':
        return 'pi pi-credit-card cdi-brand-visa';
      case 'mastercard':
        return 'pi pi-credit-card cdi-brand-mc';
      case 'amex':
        return 'pi pi-credit-card cdi-brand-amex';
      case 'discover':
        return 'pi pi-credit-card cdi-brand-discover';
      default:
        return 'pi pi-credit-card cdi-brand-unknown';
    }
  }

  public showPaymentDialog(): void {
    this.resetPaymentForm();
    this.copyCurrencyFromOrder();
    this.copyCountryFromOrder();
    this.copyPaymentAmountFromOrder();
    this.copyStreetAddress1FromOrder();
    this.copyStreetAddress2FromOrder();
    this.copyCityFromOrder();
    this.copyStateFromOrder();
    this.copyZipCodeFromOrder();
    this.applyAddressValidatorsForCountry();
    this.displayPaymentDialog = true;
  }

  public showPaymentConfirmationDialog(paymentResponse: PaymentResponse): void {
    this.paymentResponse = paymentResponse;
    this.displayPaymentConfirmationDialog = true;
  }

  public closePaymentDialog(): void {}

  public onPaymentDialogHide(): void {
    this.resetPaymentForm();
  }

  public onPaymentConfirmationDialogHide(): void {
    window.location.reload();
  }

  public onCardNumberInput(): void {
    const raw = this.paymentForm.get('cardNumber')?.value ?? '';
    const det = detectCardFromPanInput(raw);
    this.detectedBrand = det.brand;
    this.paymentForm
      .get('cardType')
      ?.setValue(det.cyberSourceCardTypeCode, { emitEvent: false });
    const cvv = this.paymentForm.get('cvv');
    cvv?.setValidators([
      Validators.required,
      cvvLengthValidator(det.cvvLength),
    ]);
    cvv?.updateValueAndValidity({ emitEvent: false });
  }

  public submitPayment(): void {
    if (this.paymentForm.invalid) {
      this.paymentForm.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const v = this.paymentForm.getRawValue();
    const line1 = (v.streetAddress1 ?? '').trim();
    const line2 = (v.streetAddress2 ?? '').trim();
    const combined = [line1, line2].filter(Boolean).join(', ');
    const payload = new PaymentData({
      lookupId: this.lookupId,
      cardNumber: stripCardNumberSpaces(v.cardNumber ?? ''),
      cardExpirationMonth: Number(v.expirationMonth),
      cardExpirationYear: Number(v.expirationYear),
      cardType: (v.cardType ?? '001').toString(),
      cardSecurityCode: (v.cvv ?? '').toString(),
      billToFirstName: (v.firstName ?? '').trim(),
      billToLastName: (v.lastName ?? '').trim(),
      billToEmail: (v.email ?? '').trim(),
      billToAddress1: combined.slice(0, 60),
      billToLocality: (v.city ?? '').trim(),
      billToAdministrativeArea: this.usesSubdivisionDropdown()
        ? (v.state ?? '').trim().toUpperCase()
        : (v.state ?? '').trim(),
      billToPostalCode: (v.zipCode ?? '').trim(),
      billToCountry: (v.country ?? '').trim().toUpperCase(),
      amount: Number(v.amount),
      currency: (v.currency ?? 'USD').toUpperCase(),
      emailAddress: (v.email ?? '').trim(),
    });

    this._data
      .add('api/Dashboard/Payment', payload, 'Processing payment ...', false)
      .subscribe({
        next: (paymentResponse) => {
          console.log('submitPayment', paymentResponse.status);
          if (paymentResponse.status === 208) {
            this.clearCardSensitiveFields();
            this.displayPaymentDialog = false;
            this._cache.showDialog.next(
              new NotificationMessage(
                DialogType.info,
                'Info',
                ['Invoice has been paid'],
                '',
              ),
            );
          } else {
            this.clearCardSensitiveFields();
            this.displayPaymentDialog = false;
            this.showPaymentConfirmationDialog(paymentResponse.body);
          }
        },
        error: (err) => {
          this.clearCardSensitiveFields();
          this.displayPaymentDialog = false;
          throw err;
        },
      });
  }

  private sanitizeSection(raw: string | null): string {
    if (raw == null || raw.trim() === '') {
      return 'NO_VALUE_PROVIDED';
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      return 'INVALID_VALUE';
    }
    if (decoded.length > LOOKUP_MAX_LEN || !SECTION_PATTERN.test(decoded)) {
      return 'INVALID_VALUE';
    }
    return decoded;
  }

  private orderAmountFromApi(): number | null {
    const raw = this.order?.orderTotal;
    if (raw == null) {
      return null;
    }
    const max = Number(raw);
    return Number.isFinite(max) ? max : null;
  }

  private refreshAmountValidators(): void {
    const amountCtrl = this.paymentForm.get('amount');
    const max = this.orderAmountFromApi();
    if (max != null) {
      amountCtrl?.setValidators([
        Validators.required,
        Validators.min(0.01),
        amountMaxValidator(max),
      ]);
    } else {
      amountCtrl?.setValidators([Validators.required, Validators.min(0.01)]);
    }
    amountCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private copyPaymentAmountFromOrder(): void {
    this.refreshAmountValidators();
    const max = this.orderAmountFromApi();
    const amountCtrl = this.paymentForm.get('amount');
    if (max != null) {
      amountCtrl?.setValue(max, { emitEvent: false });
      amountCtrl?.disable({ emitEvent: false });
    } else {
      amountCtrl?.enable({ emitEvent: false });
    }
  }

  private copyCurrencyFromOrder(): void {
    const code = (this.order?.currencyCode ?? '').toString().trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(code)) {
      this.paymentForm.get('currency')?.setValue(code, { emitEvent: false });
    }
  }

  private copyCountryFromOrder(): void {
    const country = (this.order?.dealerBillToAddress?.countryCode ?? '')
      .toString()
      .trim()
      .toUpperCase();
    if (/^[A-Z]{2}$/.test(country)) {
      this.paymentForm.patchValue({ country }, { emitEvent: false });
    }
  }

  private copyStreetAddress1FromOrder(): void {
    const streetAddress1 = this.order?.dealerBillToAddress?.streetAddress1;
    if (streetAddress1 != null) {
      this.paymentForm.patchValue(
        { streetAddress1: streetAddress1 },
        { emitEvent: false },
      );
    }
  }

  private copyStreetAddress2FromOrder(): void {
    const streetAddress2 = this.order?.dealerBillToAddress?.streetAddress2;
    if (streetAddress2 != null) {
      this.paymentForm.patchValue(
        { streetAddress2: streetAddress2 },
        { emitEvent: false },
      );
    }
  }

  private copyCityFromOrder(): void {
    const city = this.order?.dealerBillToAddress?.city;
    if (city != null) {
      this.paymentForm.patchValue({ city: city }, { emitEvent: false });
    }
  }

  private copyStateFromOrder(): void {
    const state = this.order?.dealerBillToAddress?.state;
    if (state != null) {
      this.paymentForm.patchValue({ state: state }, { emitEvent: false });
    }
  }

  private copyZipCodeFromOrder(): void {
    const zipCode = this.order?.dealerBillToAddress?.zipCode;
    if (zipCode != null) {
      this.paymentForm.patchValue({ zipCode: zipCode }, { emitEvent: false });
    }
  }

  private clearCardSensitiveFields(): void {
    this.paymentForm.patchValue(
      { cardNumber: '', cvv: '' },
      { emitEvent: false },
    );
  }

  private resetPaymentForm(): void {
    this.submitting = false;
    this.detectedBrand = 'unknown';
    this.paymentForm.reset({
      firstName: '',
      lastName: '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
      email: '',
      cardNumber: '',
      cardType: '001',
      expirationMonth: null,
      expirationYear: null,
      cvv: '',
      amount: null,
      currency: 'USD',
    });
    this.paymentForm.get('cardType')?.setValue('001', { emitEvent: false });
    this.paymentForm.get('cardType')?.disable({ emitEvent: false });
    this.paymentForm.get('currency')?.disable({ emitEvent: false });
    this.paymentForm.get('amount')?.disable({ emitEvent: false });
    this.onCardNumberInput();
    this.copyCurrencyFromOrder();
    this.copyPaymentAmountFromOrder();
    this.applyAddressValidatorsForCountry();
  }

  private applyAddressValidatorsForCountry(): void {
    const isUsOrCa = this.usesSubdivisionDropdown();
    const state = this.paymentForm.get('state');
    const zip = this.paymentForm.get('zipCode');
    if (isUsOrCa) {
      state?.setValidators([
        Validators.required,
        Validators.maxLength(60),
        stateForCountryValidator,
      ]);
      zip?.setValidators([Validators.required, zipForCountryValidator]);
    } else {
      state?.setValidators([Validators.maxLength(60), stateForCountryValidator]);
      zip?.setValidators([zipForCountryValidator]);
    }
    state?.updateValueAndValidity({ emitEvent: false });
    zip?.updateValueAndValidity({ emitEvent: false });
  }
}
